import { memo, type RefCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '../../../shared/ui/Button'
import { type Post } from '../model/contracts'
import { groupPostsByDay } from '../lib/groupPostsByDay'
import { DayHeader } from './DayHeader'
import { PostCard } from './PostCard'
import { PostSkeleton } from './PostSkeleton'
import { EmptyState } from '../../../shared/ui/States'

export const PostsFeedView = memo(function PostsFeedView(props: {
  posts: Post[]
  isFetchingNextPage: boolean
  hasNextPage: boolean
  onLoadMore: () => void
  sentinelRef: RefCallback<HTMLDivElement>
  isLoading?: boolean
}) {
  const { posts, isFetchingNextPage, hasNextPage, onLoadMore, sentinelRef, isLoading } =
    props
  const { t } = useTranslation()

  if (isLoading && posts.length === 0) {
    return (
      <div className="flex flex-col gap-8">
        {[...Array(3)].map((_, i) => (
          <PostSkeleton key={i} />
        ))}
      </div>
    )
  }

  if (posts.length === 0 && !isFetchingNextPage) {
    return <EmptyState />
  }

  const groups = groupPostsByDay(posts)

  return (
    <section aria-label="Post feed" className="flex flex-col">
      {groups.map((g) => (
        <section key={g.dateKey} aria-label={g.dateKey}>
          <DayHeader dateTime={g.dateTime} count={g.count} />
          <ul className="flex flex-col gap-3 m-0 p-0 list-none sm:gap-6 mt-3 mb-6">
            {g.posts.map((p) => (
              <li key={p.key} className="min-h-0 [contain:layout_style_paint]">
                <PostCard post={p} />
              </li>
            ))}
          </ul>
        </section>
      ))}

      {isFetchingNextPage && (
        <div className="mb-6">
          <PostSkeleton />
        </div>
      )}

      <div className="mt-8 grid place-items-center gap-4 p-2">
        <div ref={sentinelRef} aria-hidden="true" />

        {hasNextPage ? (
          <Button
            variant="primary"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            aria-label={t('common.loadMore', { defaultValue: 'Load more posts' })}
          >
            {isFetchingNextPage
              ? t('common.loadingDots', { defaultValue: 'Loading…' })
              : t('common.loadMore', { defaultValue: 'Load more' })}
          </Button>
        ) : (
          posts.length > 0 && (
            <div className="text-xs opacity-70">
              {t('common.endOfFeed', { defaultValue: 'End of feed' })}
            </div>
          )
        )}
      </div>
    </section>
  )
})
